import { ROLES as R } from "../core/roles";
import { everyoneBut, lud, rule } from "../core/rule";
import { SCOPES } from "../core/scope";

// UI hints only: the menu backend (menuAccess) enforces the same rules
// again. "audience" permissions describe which side of the menu a role works
// on; branch users are restricted to their assigned branches. `dm` has no menu
// access at all (NA-021).
const LUD_SIDE = lud(R.MENU_SP, R.AM, R.DMM);
const RESTAURANT_MANAGERS = [R.STORE_L, R.STORE];
const VIEWERS = lud(R.FA, R.DMM, R.AM, R.MENU_SP, R.FEEDUS, R.SALES, R.STORE_L, R.STORE, R.BRANCH, R.NEW);
// Send to App / AI import / Copilot (approved): ceo-level, dmm, am, store, storeL.
// menuSp keeps Send to App (it held it before); dm has no menu access at all (NA-021).
// All three also need the selected restaurant to be in the user's scope.
const SEND_ROLES = lud(R.MENU_SP, R.AM, R.DMM, R.STORE_L, R.STORE);
const AI_ROLES = lud(R.AM, R.DMM, R.STORE_L, R.STORE);
const restaurantScoped = (roles) => rule(roles, { scope: SCOPES.RESTAURANT });
const WRITERS = lud(R.MENU_SP, R.AM, R.DMM, ...RESTAURANT_MANAGERS);

export const menuPermissions = {
  view: "menu.view",
  manage: "menu.manage",
  manageAdmin: "menu.manage.admin",
  audience: {
    lud: "menu.audience.lud",
    restaurantManager: "menu.audience.restaurantManager",
    prospect: "menu.audience.prospect",
    branchUser: "menu.audience.branchUser",
  },
  branch: { visibility: "menu.branch.visibility" },
  addition: { view: "menu.addition.view" },
  summary: { view: "menu.summary.view" },
  send: "menu.send",
  ai: { import: "menu.ai.import" },
  copilot: "menu.copilot.use",
};

export const menuGrants = {
  // `branch` users additionally need a valid assigned branch (NA-035); no other role is affected
  "menu.view": rule(VIEWERS, { requiresBranch: true }),
  // create / edit / archive menus, sections, items, additions, discounts
  // (needs access to the restaurant of the menu being changed)
  "menu.manage": restaurantScoped(WRITERS),
  "menu.manage.admin": restaurantScoped(LUD_SIDE),
  "menu.audience.lud": rule(LUD_SIDE),
  "menu.audience.restaurantManager": rule(RESTAURANT_MANAGERS),
  // a prospective client sees the restaurant-side menu screens, read-only until its subscription is active
  "menu.audience.prospect": rule([R.NEW]),
  "menu.audience.branchUser": rule([R.BRANCH], { requiresBranch: true }),
  // toggling items on / off per branch (managers and branch users)
  // resource: { restaurantId, branchId } (branchId = branch name, as stored in user.assignedBranch)
  "menu.branch.visibility": rule([...WRITERS, R.BRANCH], { scope: SCOPES.BRANCH, requiresBranch: true }),
  // additions are hidden from branch users (NA-004, approved)
  "menu.addition.view": rule([...everyoneBut(R.BRANCH), R.NEW]),
  // the selected-menu summary (overview cards above the menu): every menu viewer except branch users
  "menu.summary.view": rule(VIEWERS.filter((role) => role !== R.BRANCH)),
  // send items to a delivery app (needs access to the selected restaurant)
  "menu.send": restaurantScoped(SEND_ROLES),
  // AI menu import and Menu Copilot (the AI wallet / usage limits still apply server-side)
  "menu.ai.import": restaurantScoped(AI_ROLES),
  "menu.copilot.use": restaurantScoped(AI_ROLES),
};
